// Conversiones mejoradas: con qué empareja Google Ads una cotización y el clic
// que la trajo.
//
// El correo y el celular salen del navegador convertidos en hash, no en claro.
// Google acepta las dos formas —si le llegan en claro, los hashea su propia
// etiqueta—, pero en claro pasan antes por el dataLayer, que es un objeto que
// puede leer cualquier otra etiqueta del contenedor. Hashear acá significa que
// el correo de un cliente no queda escrito en una variable global de la página
// ni un instante.
//
// Se manda solo correo y celular. El nombre y la dirección también se aceptan,
// pero Google no los empareja sin código postal, y en Chile el formulario no lo
// pide: mandarlos sería entregar más datos personales a cambio de nada.

// SHA-256 en hexadecimal minúsculo, que es el formato que pide Google.
async function sha256Hex (value) {
  const bytes = new TextEncoder().encode(value)
  const digest = await crypto.subtle.digest('SHA-256', bytes)

  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('')
}

// Normalización que pide Google para el correo: sin espacios alrededor y en
// minúsculas. Dos escrituras del mismo correo tienen que dar el mismo hash, o
// no empareja nada.
export function normalizeEmail (value) {
  return String(value ?? '').trim().toLowerCase()
}

// E.164: un "+", el código de país y el número, sin espacios ni guiones.
// normalizePhone ya deja "56912345678" —es el formato que guarda la base—, así
// que acá solo falta el signo.
export function toE164 (value) {
  const digits = String(value ?? '').replace(/\D/g, '')

  return digits ? `+${digits}` : ''
}

// Los nombres que usa Google para los campos ya hasheados. Se respetan tal cual
// para que quien configure Tag Manager los reconozca sin traducir nada.
//
// Devuelve null cuando el navegador no puede hashear: crypto.subtle solo existe
// en contextos seguros, así que en un sitio servido por HTTP no está. Una
// conversión sin emparejar sigue siendo una conversión; una que no se envía
// porque el hash falló, no.
export async function hashedUserData ({ email, phone } = {}) {
  if (!globalThis.crypto?.subtle) return null

  const normalizedEmail = normalizeEmail(email)
  const normalizedPhone = toE164(phone)

  try {
    const [ hashedEmail, hashedPhone ] = await Promise.all([
      normalizedEmail ? sha256Hex(normalizedEmail) : null,
      normalizedPhone ? sha256Hex(normalizedPhone) : null
    ])

    const data = {}
    if (hashedEmail) data.sha256_email_address = hashedEmail
    if (hashedPhone) data.sha256_phone_number = hashedPhone

    return Object.keys(data).length ? data : null
  } catch {
    return null
  }
}
